<template>
  <b-modal
    :active.sync="showModal"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    aria-modal
    has-modal-card
    :width="960"
    @close="closeModal"
  >
    <div class="modal-card" style="width: 900px; max-height: 90vh;">
      <header class="modal-card-head">
        <p class="modal-card-title">
          <span class="icon"><i class="mdi mdi-file-document-alert"></i></span>
          Missing Assets Report
        </p>
        <button class="delete" aria-label="close" @click="closeModal"></button>
      </header>

      <section class="modal-card-body">
        <!-- Filters Section -->
        <div class="box mb-4">
          <div class="columns is-multiline">
            <div class="column is-4">
              <b-field label="Channel">
                <b-select v-model="selectedChannelId" placeholder="Select channel" expanded>
                  <option v-for="channel in channels" :key="channel.id" :value="channel.id">
                    {{ channel.name }}
                  </option>
                </b-select>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label="Start Date">
                <b-datepicker
                  v-model="startDate"
                  placeholder="Select date"
                  icon="calendar"
                  :max-date="endDate"
                ></b-datepicker>
              </b-field>
            </div>
            <div class="column is-3">
              <b-field label="End Date">
                <b-datepicker
                  v-model="endDate"
                  placeholder="Select date"
                  icon="calendar"
                  :min-date="startDate"
                ></b-datepicker>
              </b-field>
            </div>
            <div class="column is-2">
              <b-field label="&nbsp;">
                <b-button
                  type="is-primary"
                  icon-left="magnify"
                  :loading="loading"
                  @click="generateReport"
                  expanded
                >
                  Generate
                </b-button>
              </b-field>
            </div>
          </div>

          <!-- Status Filter -->
          <div class="columns">
            <div class="column">
              <b-field label="Filter by Status">
                <b-checkbox-button
                  v-for="status in availableStatuses"
                  :key="status.value"
                  v-model="selectedStatuses"
                  :native-value="status.value"
                  :type="status.type"
                  size="is-small"
                >
                  <span class="icon is-small"><i :class="'mdi ' + status.icon"></i></span>
                  <span>{{ status.label }}</span>
                </b-checkbox-button>
              </b-field>
            </div>
          </div>
        </div>

        <!-- Summary Section (show after report generated) -->
        <div v-if="reportData" class="mb-4">
          <div class="level">
            <div class="level-left">
              <div class="level-item">
                <h4 class="title is-5">
                  {{ reportData.channel.name }} - {{ formatDateRange }}
                </h4>
              </div>
            </div>
            <div class="level-right">
              <div class="level-item" v-if="bulkConfirmableCount > 0">
                <b-button
                  type="is-success"
                  icon-left="check-all"
                  size="is-small"
                  :loading="bulkConfirming"
                  @click="bulkConfirmFiltered"
                  :title="'Confirms all Confirm-Needed items currently visible. Each ep is gated server-side: must be soft_linked + pending_review + segments done (needs_soft_segmentation=0). QC failures auto-override on Confirm — same as clicking the single Confirm button per episode (stitch worker already applied EBU R128 loudnorm to output).'"
                >
                  Confirm All ({{ bulkConfirmableCount }})
                </b-button>
              </div>
              <div class="level-item">
                <b-button
                  type="is-link"
                  icon-left="download"
                  size="is-small"
                  @click="downloadCsv"
                >
                  Download CSV
                </b-button>
              </div>
            </div>
          </div>

          <!-- Status Summary (2026-06-10: surface all blocker buckets so
               operators can see the work that was previously hidden behind
               the "Ready for TX" green badge). -->
          <div class="tags are-medium mb-4">
            <span class="tag" :class="getSummaryTagClass('missing')">
              <span class="icon"><i class="mdi mdi-file-remove"></i></span>
              <span>Missing Files: {{ reportData.summary.byStatus.missing || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('needs_link_capture')" title="File exists in storage but workflow status is not_linked — required-files endpoint refuses to download, playout VM never gets the file">
              <span class="icon"><i class="mdi mdi-link-variant-plus"></i></span>
              <span>Link Capture: {{ reportData.summary.byStatus.needs_link_capture || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('needs_link_repair')" title="Pre-2026-04-05 legacy trap: tx_workflow_status=pending_review but link_status=not_linked. UI's Confirm button is hidden until you re-link via 'Link existing TX file' (which captures etag and flips link_status)">
              <span class="icon"><i class="mdi mdi-link-off"></i></span>
              <span>Re-Link Needed: {{ reportData.summary.byStatus.needs_link_repair || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('pending_confirm')">
              <span class="icon"><i class="mdi mdi-clipboard-alert"></i></span>
              <span>Confirm Needed: {{ reportData.summary.byStatus.pending_confirm || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('qc_override_needed')">
              <span class="icon"><i class="mdi mdi-shield-alert"></i></span>
              <span>QC Override: {{ reportData.summary.byStatus.qc_override_needed || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('version_changed')">
              <span class="icon"><i class="mdi mdi-history"></i></span>
              <span>V2 Re-Confirm: {{ reportData.summary.byStatus.version_changed || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('qc_failed')">
              <span class="icon"><i class="mdi mdi-close-circle"></i></span>
              <span>Processing Failed: {{ reportData.summary.byStatus.qc_failed || 0 }}</span>
            </span>
            <span class="tag" :class="getSummaryTagClass('ready')">
              <span class="icon"><i class="mdi mdi-check-bold"></i></span>
              <span>Air Ready: {{ reportData.summary.byStatus.ready || 0 }}</span>
            </span>
            <span class="tag" :class="softSegmentationCount > 0 ? 'is-warning' : 'is-light'" :title="softSegmentationCount > 0 ? 'Episodes using default segment durations' : ''">
              <span class="icon"><i class="mdi mdi-clock-alert"></i></span>
              <span>Needs Seg Review: {{ softSegmentationCount }}</span>
            </span>
            <span class="tag is-light">
              <span class="icon"><i class="mdi mdi-sigma"></i></span>
              <span>Total Scheduled: {{ reportData.summary.total }}</span>
            </span>
          </div>
        </div>

        <!-- Results Table -->
        <div v-if="reportData" class="table-container" style="max-height: 400px; overflow-y: auto;">
          <table class="table is-fullwidth is-striped is-hoverable is-narrow">
            <thead>
              <tr>
                <th style="width: 100px;">Date/Time</th>
                <th>Title</th>
                <th style="width: 80px;">S/E</th>
                <th style="width: 140px;">TX Filename</th>
                <th style="width: 120px;">Status</th>
                <th style="width: 50px;" title="Soft Segmentation Warning">Seg</th>
                <th style="width: 60px;">QC</th>
                <th style="width: 80px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredItems" :key="item.scheduleId" :class="getRowClass(item.status)">
                <td>
                  <small>{{ item.scheduledDate }}<br/>{{ item.scheduledTime }}</small>
                </td>
                <td>
                  <strong>{{ item.mediaTitle }}</strong>
                  <span v-if="item.isCrossRegion" class="tag is-warning is-light is-size-7 ml-1" :title="'tx_file_path is outside the playout-source bucket — Traffic Agent will still fetch it but cross-region downloads are slower. Optional to migrate for performance.'">
                    <span class="icon is-small"><i class="mdi mdi-earth"></i></span>
                    <span>Cross-Region</span>
                  </span>
                  <span v-if="item.episodeId" class="has-text-grey is-size-7">
                    <br/>{{ item.episodeCode || `E${item.episodeNumber}` }}
                  </span>
                </td>
                <td>
                  <span v-if="item.season || item.episodeNumber">
                    <small>S{{ item.season || '?' }} E{{ item.episodeNumber || '?' }}</small>
                  </span>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <small v-if="item.txFilename" class="has-text-info">{{ item.txFilename }}</small>
                  <small v-else class="has-text-grey">Not set</small>
                </td>
                <td>
                  <span class="tag" :class="getStatusTagClass(item.status)">
                    <span class="icon is-small"><i :class="'mdi ' + getStatusIcon(item.status)"></i></span>
                    <span>{{ getStatusLabel(item.status) }}</span>
                  </span>
                </td>
                <td>
                  <span v-if="item.needsSoftSegmentation" class="tag is-warning is-light" title="Segments using default durations - needs manual segmentation review">
                    <span class="icon is-small"><i class="mdi mdi-clock-alert"></i></span>
                  </span>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <span v-if="item.qcIssueCount > 0" class="tag is-danger is-light">
                    {{ item.qcIssueCount }} issues
                  </span>
                  <span v-else-if="item.status === 'ready'" class="tag is-success is-light">
                    OK
                  </span>
                  <span v-else class="has-text-grey">-</span>
                </td>
                <td>
                  <div class="buttons are-small">
                    <b-button
                      v-if="item.episodeId"
                      type="is-link"
                      icon-left="pencil"
                      size="is-small"
                      tag="router-link"
                      :to="`/edit-episode/${item.episodeId}`"
                      title="Edit Episode"
                    />
                    <b-button
                      v-else
                      type="is-link"
                      icon-left="pencil"
                      size="is-small"
                      tag="router-link"
                      :to="`/media/${item.mediaId}`"
                      title="Edit Media"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <p v-if="filteredItems.length === 0" class="has-text-centered has-text-grey py-4">
            No items match the selected filters
          </p>
        </div>

        <!-- Empty state -->
        <div v-if="!reportData && !loading" class="has-text-centered py-6">
          <span class="icon is-large has-text-grey-light">
            <i class="mdi mdi-file-document-alert mdi-48px"></i>
          </span>
          <p class="has-text-grey mt-2">Select a channel and date range, then click Generate</p>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="has-text-centered py-6">
          <b-loading :is-full-page="false" :active="loading"></b-loading>
        </div>
      </section>

      <footer class="modal-card-foot">
        <b-button @click="closeModal">Close</b-button>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import missingAssetsReportApi from '@/api/missingAssetsReport'
import { format, addDays } from 'date-fns'

export default {
  name: 'MissingAssetsReportModal',
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      showModal: this.show,
      channels: [],
      selectedChannelId: null,
      startDate: new Date(),
      endDate: addDays(new Date(), 7),
      // 2026-06-10: Default to ALL blocker buckets so operator sees every
      // actionable item. 'ready' excluded by default (re-enable to verify).
      selectedStatuses: ['missing', 'needs_link_capture', 'needs_link_repair', 'pending_confirm', 'qc_override_needed', 'version_changed', 'qc_failed'],
      bulkConfirming: false,
      reportData: null,
      availableStatuses: [
        { value: 'missing',            label: 'Missing Files',      type: 'is-danger',  icon: 'mdi-file-remove' },
        { value: 'needs_link_capture', label: 'Link Capture Needed',type: 'is-warning', icon: 'mdi-link-variant-plus' },
        { value: 'needs_link_repair',  label: 'Re-Link (Legacy)',   type: 'is-warning', icon: 'mdi-link-off' },
        { value: 'pending_confirm',    label: 'Confirm Needed',     type: 'is-warning', icon: 'mdi-clipboard-alert' },
        { value: 'qc_override_needed', label: 'QC Override Needed', type: 'is-warning', icon: 'mdi-shield-alert' },
        { value: 'version_changed',    label: 'V2 — Re-Confirm',   type: 'is-warning', icon: 'mdi-history' },
        { value: 'qc_failed',          label: 'Processing Failed',  type: 'is-danger',  icon: 'mdi-close-circle' },
        { value: 'ready',              label: 'Air Ready',          type: 'is-success', icon: 'mdi-check-bold' }
      ]
    }
  },
  computed: {
    formatDateRange() {
      if (!this.startDate || !this.endDate) return ''
      return `${format(this.startDate, 'MMM d')} - ${format(this.endDate, 'MMM d, yyyy')}`
    },
    filteredItems() {
      if (!this.reportData?.items) return []
      if (this.selectedStatuses.length === 0) return this.reportData.items
      return this.reportData.items.filter(item => this.selectedStatuses.includes(item.status))
    },
    softSegmentationCount() {
      if (!this.reportData?.items) return 0
      return this.reportData.items.filter(item => item.needsSoftSegmentation).length
    },
    // Items currently visible (after filter) that are in pending_confirm and
    // have episode IDs — i.e. exactly the set bulk-Confirm will act on.
    // We filter client-side here so the button count matches what the user
    // sees; the backend re-applies its own server-side gates.
    bulkConfirmableItems() {
      if (!this.reportData?.items) return []
      return this.filteredItems.filter(item =>
        item.status === 'pending_confirm' && !!item.episodeId
      )
    },
    bulkConfirmableCount() {
      return this.bulkConfirmableItems.length
    }
  },
  watch: {
    show() {
      this.showModal = this.show
      if (this.show && this.channels.length === 0) {
        this.loadChannels()
      }
    }
  },
  async created() {
    await this.loadChannels()
  },
  methods: {
    async loadChannels() {
      try {
        const result = await missingAssetsReportApi.getReportChannels()
        this.channels = result.channels
        if (this.channels.length > 0 && !this.selectedChannelId) {
          this.selectedChannelId = this.channels[0].id
        }
      } catch (error) {
        console.error('Failed to load channels:', error)
      }
    },

    async generateReport() {
      if (!this.selectedChannelId) {
        this.$buefy.toast.open({
          message: 'Please select a channel',
          type: 'is-warning'
        })
        return
      }

      this.loading = true
      try {
        const params = {
          channelId: this.selectedChannelId,
          startDate: format(this.startDate, 'yyyy-MM-dd'),
          endDate: format(this.endDate, 'yyyy-MM-dd')
        }
        this.reportData = await missingAssetsReportApi.getMissingAssetsReport(params)
      } catch (error) {
        console.error('Failed to generate report:', error)
        this.$buefy.toast.open({
          message: 'Failed to generate report',
          type: 'is-danger'
        })
      } finally {
        this.loading = false
      }
    },

    /**
     * Bulk-confirm every Confirm-Needed item currently visible in the report.
     * Server-side re-applies gates (soft_linked + pending_review + soft-seg
     * done) and skips any that fail with a reason. Audit log captures one
     * row per confirmed ep with _bulk_action marker.
     *
     * QC failures auto-override on Confirm (same as single Confirm) —
     * operator policy: stitch worker applied EBU R128 loudnorm to output,
     * so QC failures on input source files are no longer blocking the OK
     * stitched file in CPT.
     */
    async bulkConfirmFiltered() {
      const items = this.bulkConfirmableItems
      if (items.length === 0) return

      const proceed = await new Promise(resolve => {
        this.$buefy.dialog.confirm({
          title: 'Confirm all visible Confirm-Needed items',
          message: `<p>You are about to <strong>Confirm ${items.length} episode${items.length === 1 ? '' : 's'}</strong> for playout in one action.</p>
            <p class="mt-2 is-size-7 has-text-grey">For each ep: link_status → confirmed, tx_workflow_status → approved, QC auto-overridden (stitch worker applied EBU R128 loudnorm to output). Soft-seg-flagged eps are skipped server-side. Audit log captures every confirmation with bulk-action marker.</p>`,
          confirmText: `Confirm ${items.length}`,
          cancelText: 'Cancel',
          type: 'is-success',
          hasIcon: true,
          icon: 'check-all',
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })
      if (!proceed) return

      this.bulkConfirming = true
      try {
        const episodeIds = items.map(i => i.episodeId)
        const result = await missingAssetsReportApi.bulkConfirmEpisodes(episodeIds)
        const c = result?.counts || {}
        this.$buefy.toast.open({
          message: `Confirmed ${c.confirmed || 0} ep${c.confirmed === 1 ? '' : 's'}${c.skipped > 0 ? `, skipped ${c.skipped} (see server logs)` : ''}`,
          type: c.confirmed > 0 ? 'is-success' : 'is-warning',
          duration: 5000
        })
        // Refresh report so confirmed eps move to Air Ready (or fall out of
        // the current filter)
        await this.generateReport()
      } catch (e) {
        console.error('Bulk confirm failed:', e)
        this.$buefy.toast.open({
          message: `Bulk confirm failed: ${e.response?.data?.error || e.message}`,
          type: 'is-danger',
          duration: 6000
        })
      } finally {
        this.bulkConfirming = false
      }
    },

    async downloadCsv() {
      try {
        const params = {
          channelId: this.selectedChannelId,
          startDate: format(this.startDate, 'yyyy-MM-dd'),
          endDate: format(this.endDate, 'yyyy-MM-dd')
        }
        if (this.selectedStatuses.length > 0) {
          params.statusFilter = this.selectedStatuses.join(',')
        }
        // Prefer the channel name from the loaded report (fresh) so the filename
        // matches what's on screen. Falls back to the channels list lookup if
        // the user hasn't generated yet — shouldn't happen since Download is
        // hidden until reportData is set, but be defensive.
        const channelName =
          this.reportData?.channel?.name ||
          this.channels.find(c => c.id === this.selectedChannelId)?.name ||
          ''
        await missingAssetsReportApi.downloadMissingAssetsReportCsv(params, channelName)
        this.$buefy.toast.open({
          message: 'Report downloaded',
          type: 'is-success'
        })
      } catch (error) {
        console.error('Failed to download report:', error)
        this.$buefy.toast.open({
          message: 'Failed to download report',
          type: 'is-danger'
        })
      }
    },

    // 2026-06-10: Added pending_confirm, qc_override_needed, version_changed
    // as first-class display states (previously collapsed to "ready" green).
    // 2026-06-10: Added pending_confirm, qc_override_needed, version_changed,
    // needs_link_capture as first-class display states (previously collapsed to
    // "ready" green, hiding 296+ Siyaya / 29 Media24 actionable blockers).
    getStatusTagClass(status) {
      const classes = {
        missing: 'is-danger',
        qc_failed: 'is-danger',
        needs_link_capture: 'is-warning',
        needs_link_repair: 'is-warning',
        pending_confirm: 'is-warning',
        qc_override_needed: 'is-warning',
        version_changed: 'is-warning',
        ready: 'is-success',
        overridden: 'is-success is-light',
        processing: 'is-info',
        queued: 'is-info',
        in_qc: 'is-info'
      }
      return classes[status] || 'is-light'
    },

    getStatusIcon(status) {
      const icons = {
        missing: 'mdi-file-remove',
        qc_failed: 'mdi-close-circle',
        needs_link_capture: 'mdi-link-variant-plus',
        needs_link_repair: 'mdi-link-off',
        pending_confirm: 'mdi-clipboard-alert',
        qc_override_needed: 'mdi-shield-alert',
        version_changed: 'mdi-history',
        ready: 'mdi-check-bold',
        overridden: 'mdi-check-decagram',
        processing: 'mdi-progress-clock',
        queued: 'mdi-tray-arrow-down',
        in_qc: 'mdi-magnify-scan'
      }
      return icons[status] || 'mdi-help-circle'
    },

    getStatusLabel(status) {
      const labels = {
        missing: 'Missing Files',
        qc_failed: 'Processing Failed',
        needs_link_capture: 'Link Capture Needed',
        needs_link_repair: 'Re-Link Needed',
        pending_confirm: 'Confirm Needed',
        qc_override_needed: 'QC Override Needed',
        version_changed: 'V2 — Re-Confirm',
        ready: 'Air Ready',
        overridden: 'QC Overridden',
        processing: 'Processing',
        queued: 'Queued',
        in_qc: 'QC Running'
      }
      return labels[status] || status
    },

    getRowClass(status) {
      const classes = {
        missing: 'has-background-danger-light',
        qc_failed: 'has-background-danger-light',
        needs_link_capture: 'has-background-warning-light',
        needs_link_repair: 'has-background-warning-light',
        pending_confirm: 'has-background-warning-light',
        qc_override_needed: 'has-background-warning-light',
        version_changed: 'has-background-warning-light'
      }
      return classes[status] || ''
    },

    getSummaryTagClass(status) {
      const count = this.reportData?.summary?.byStatus?.[status] || 0
      if (count === 0) return 'is-light'

      const classes = {
        missing: 'is-danger',
        qc_failed: 'is-danger',
        needs_link_capture: 'is-warning',
        needs_link_repair: 'is-warning',
        pending_confirm: 'is-warning',
        qc_override_needed: 'is-warning',
        version_changed: 'is-warning',
        ready: 'is-success'
      }
      return classes[status] || 'is-light'
    },

    closeModal() {
      this.showModal = false
      this.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.modal-card {
  max-width: 95vw;
}

.table-container {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
}

.table thead th {
  position: sticky;
  top: 0;
  background: #f5f5f5;
  z-index: 1;
}

.tag .icon {
  margin-right: 0.25rem;
}

.tags .tag {
  margin-right: 0.5rem;
}
</style>
